// src/pages/MarketplaceIntegration/ErrorReporting.jsx

import React, { useState, useEffect } from 'react';
import useMarketplaceIntegration from './useMarketplaceIntegration';

const ErrorReporting = ({ marketplaces }) => {
  const [errors, setErrors] = useState([]);
  const [selectedMarketplace, setSelectedMarketplace] = useState('');
  const [filter, setFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { getErrorLogs } = useMarketplaceIntegration();

  useEffect(() => {
    fetchErrorLogs();
  }, []);

  const fetchErrorLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Prepare filters
      const filters = {};
      if (selectedMarketplace) filters.marketplace = selectedMarketplace;
      if (filter !== 'all') filters.type = filter;
      if (dateRange.start) filters.startDate = new Date(dateRange.start);
      if (dateRange.end) filters.endDate = new Date(dateRange.end);
      
      const errorLogs = await getErrorLogs(filters);
      setErrors(errorLogs);
    } catch (err) {
      console.error('Error fetching error logs:', err);
      setError('Hata raporları alınırken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Refetch when filters change
  useEffect(() => {
    fetchErrorLogs();
  }, [selectedMarketplace, filter, dateRange]);

  const handleMarketplaceChange = (e) => {
    setSelectedMarketplace(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return '#EF4444';
      case 'medium': return '#F59E0B';
      case 'low': return '#10B981';
      default: return '#9CA3AF';
    }
  };

  const getSeverityText = (severity) => {
    switch (severity) {
      case 'high': return 'Yüksek';
      case 'medium': return 'Orta';
      case 'low': return 'Düşük';
      default: return 'Bilinmiyor';
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case 'stock_sync': return 'Stok Senkronizasyonu';
      case 'order_import': return 'Sipariş İçe Aktarma';
      case 'auth': return 'Kimlik Doğrulama';
      case 'invoice_export': return 'Fatura Dışa Aktarma';
      default: return type;
    }
  };

  const filteredErrors = errors.filter(error => {
    // Filter by marketplace
    if (selectedMarketplace && error.marketplace !== selectedMarketplace) {
      return false;
    }
    
    // Filter by severity
    if (filter !== 'all' && error.severity !== filter) {
      return false;
    }
    
    // Filter by date range
    if (dateRange.start && new Date(error.createdAt?.toDate()) < new Date(dateRange.start)) {
      return false;
    }
    
    if (dateRange.end && new Date(error.createdAt?.toDate()) > new Date(dateRange.end)) {
      return false;
    }
    
    return true;
  });

  if (loading && errors.length === 0) {
    return <div className="error-reporting">Yükleniyor...</div>;
  }

  if (error) {
    return <div className="error-reporting">Hata: {error}</div>;
  }

  return (
    <div className="error-reporting">
      <div className="reporting-header">
        <h2>Hata Raporları</h2>
        <p>Pazaryeri entegrasyonlarında oluşan hataları görüntüleyin ve yönetin</p>
      </div>

      <div className="reporting-filters">
        <div className="filter-group">
          <label htmlFor="marketplace-filter">Pazaryeri</label>
          <select
            id="marketplace-filter"
            value={selectedMarketplace}
            onChange={handleMarketplaceChange}
          >
            <option value="">Tümü</option>
            {marketplaces.map(marketplace => (
              <option key={marketplace.id} value={marketplace.name.toLowerCase()}>
                {marketplace.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="severity-filter">Önem Derecesi</label>
          <select
            id="severity-filter"
            value={filter}
            onChange={handleFilterChange}
          >
            <option value="all">Tümü</option>
            <option value="high">Yüksek</option>
            <option value="medium">Orta</option>
            <option value="low">Düşük</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="date-start">Başlangıç Tarihi</label>
          <input
            type="date"
            id="date-start"
            value={dateRange.start}
            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="date-end">Bitiş Tarihi</label>
          <input
            type="date"
            id="date-end"
            value={dateRange.end}
            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
          />
        </div>
      </div>

      <div className="error-table-container">
        {filteredErrors.length === 0 ? (
          <div className="empty-state">
            <p>Seçilen kriterlere uygun hata bulunamadı</p>
          </div>
        ) : (
          <table className="error-table">
            <thead>
              <tr>
                <th>Tarih/Saat</th>
                <th>Pazaryeri</th>
                <th>Hata Türü</th>
                <th>Önem Derecesi</th>
                <th>Hata Mesajı</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredErrors.map(error => (
                <tr key={error.id}>
                  <td>{error.createdAt?.toDate().toLocaleString('tr-TR')}</td>
                  <td>{error.marketplace}</td>
                  <td>{getTypeText(error.type)}</td>
                  <td>
                    <span 
                      className="severity-badge"
                      style={{ backgroundColor: getSeverityColor(error.severity) }}
                    >
                      {getSeverityText(error.severity)}
                    </span>
                  </td>
                  <td className="error-message">{error.message}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="retry-button">Yeniden Dene</button>
                      <button className="details-button">Detaylar</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="error-summary">
        <h3>Hata Özeti</h3>
        <div className="summary-content">
          <div className="summary-item">
            <span className="summary-label">Toplam Hata:</span>
            <span className="summary-value">{errors.length}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Yüksek Öncelikli:</span>
            <span className="summary-value" style={{ color: '#EF4444' }}>
              {errors.filter(e => e.severity === 'high').length}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Orta Öncelikli:</span>
            <span className="summary-value" style={{ color: '#F59E0B' }}>
              {errors.filter(e => e.severity === 'medium').length}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Düşük Öncelikli:</span>
            <span className="summary-value" style={{ color: '#10B981' }}>
              {errors.filter(e => e.severity === 'low').length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorReporting;